import type { ReactNode } from 'react';
import { SimpleCoffeeMark } from '../../brand/SimpleCoffeeMark';
import { Button } from './Button';
import './States.css';

export interface LoadingStateProps {
  /** What is being loaded, e.g. "Brewing your profile". */
  label?: string;
}

/** Loading placeholder. The mark appears here — sparingly (section 13). */
export function LoadingState({ label = 'Brewing…' }: LoadingStateProps) {
  return (
    <div className="sc-state" role="status" aria-live="polite">
      <span className="sc-state__mark sc-state__mark--pulse">
        <SimpleCoffeeMark size={56} title={null} />
      </span>
      <p className="sc-state__title">{label}</p>
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  illustration?: ReactNode;
}

export function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
  illustration,
}: EmptyStateProps) {
  return (
    <div className="sc-state">
      <span className="sc-state__mark">
        {illustration ?? <SimpleCoffeeMark size={56} title={null} />}
      </span>
      <p className="sc-state__title">{title}</p>
      {description && <p className="sc-state__description">{description}</p>}
      {actionLabel && onAction && (
        <Button variant="primary" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  /** Correlation id so support can find the request in the logs. */
  requestId?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'We could not complete that request. Please try again.',
  onRetry,
  requestId,
}: ErrorStateProps) {
  return (
    <div className="sc-state" role="alert">
      <span className="sc-state__mark" aria-hidden="true">
        ⚠️
      </span>
      <p className="sc-state__title">{title}</p>
      <p className="sc-state__description">{description}</p>
      {requestId && <p className="sc-state__meta">Request ID: {requestId}</p>}
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}
